// Single source of truth for which deck-hosting sites we accept imports
// from, and for pre-flight validation of a candidate import URL. Used by
// the submit form and the ?ingest= deep-link flow so both surfaces accept
// exactly the same URLs and emit the same error copy.

// `label` is the domain, shown in the supported-sites list and the rejection
// copy. `name` is the site's own brand casing, used anywhere we name the source
// in prose (see getDeckSourceName).
export const SUPPORTED_SITES = [
    { host: 'moxfield.com/', label: 'moxfield.com', name: 'Moxfield' },
    { host: 'archidekt.com/', label: 'archidekt.com', name: 'Archidekt' },
    { host: 'tappedout.net/', label: 'tappedout.net', name: 'TappedOut' },
    { host: 'manabox.app/', label: 'manabox.app', name: 'ManaBox' },
    // { host: 'mtggoldfish.com/', label: 'mtggoldfish.com', name: 'MTGGoldfish' },
    { host: 'scryfall.com/', label: 'scryfall.com', name: 'Scryfall' },
    { host: 'topdecked.com/', label: 'topdecked.com', name: 'TopDecked' },
    { host: 'topdeck.gg/', label: 'topdeck.gg', name: 'TopDeck.gg' },
    { host: 'hareruyamtg.com/', label: 'hareruyamtg.com', name: 'Hareruya' },
    { host: 'commanderview.app/', label: 'commanderview.app', name: 'CommanderView' },
    { host: 'mythichub.com/', label: 'mythichub.com', name: 'MythicHub' },
];

const UNSUPPORTED_PATHS = [
    { pattern: /archidekt\.com\/folders/, message: 'Folder URIs not supported' },
    { pattern: /archidekt\.com\/compare/, message: 'Compare URIs not supported' },
    { pattern: /archidekt\.com\/articles/, message: 'Sandbox URIs not supported' },
    { pattern: /archidekt\.com\/snapshots/, message: 'Snapshot URIs not supported' },
    { pattern: /archidekt\.com\/sandbox/, message: 'Sandbox URIs not supported' },
    { pattern: /archidekt\.com\/search/, message: 'Search URIs not supported' },
    // { pattern: /mtggoldfish\.com\/articles/, message: 'MTGGoldfish articles not supported' },
    { pattern: /\/playtest/, message: 'Playtest URIs not supported' },
];

// Returns an error message when the URL can't be imported, or null when
// it's good to send to the backend.
export function validateDeckImportUrl(url) {
    const cleaned = url ? url.trim() : '';
    if (!cleaned) return 'No deck URL provided';

    for (const { pattern, message } of UNSUPPORTED_PATHS) {
        if (pattern.test(cleaned)) return message;
    }

    const hostHit = SUPPORTED_SITES.some(({ host }) => cleaned.includes(host));
    if (!hostHit) {
        return `Sorry, we currently only accept deck lists from ${SUPPORTED_SITES.slice(0, -1)
            .map((s) => s.label)
            .join(', ')}, and ${SUPPORTED_SITES[SUPPORTED_SITES.length - 1].label}.`;
    }

    return null;
}

// Brand-cased display name for a deck's stored `source` (e.g.
// "http://www.tappedout.net" -> "TappedOut", "https://mythichub.com" ->
// "MythicHub"). Sources we no longer import from still show up on old decks,
// so fall back to title-casing the domain rather than rendering nothing.
export function getDeckSourceName(source) {
    const cleaned = String(source || '').toLowerCase();
    if (!cleaned) return '';

    const site = SUPPORTED_SITES.find(({ host }) => cleaned.includes(host.replace(/\/$/, '')));
    if (site) return site.name;

    const domain = /[a-z0-9-]*(?=\.[a-z]*$)/.exec(cleaned)?.[0] || '';
    return domain ? `${domain.charAt(0).toUpperCase()}${domain.slice(1)}` : '';
}
